import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  footer?: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  className?: string;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => {
  return (
    <div className={`sticky bottom-0 z-10 flex justify-end gap-4 p-4 md:p-6 bg-card/95 backdrop-blur-sm border-t border-primary/10 ${className}`}>
      {children}
    </div>
  );
};

export const ModalButton: React.FC<ModalButtonProps> = ({ 
  children, 
  onClick, 
  variant = 'secondary',
  disabled = false,
  className = ''
}) => {
  const baseClasses = "px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";
  const variantClasses = variant === 'primary' 
    ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20" 
    : "bg-secondary/80 text-secondary-foreground hover:bg-secondary/60 border border-primary/10";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${className}`}
    >
      {children}
    </button>
  );
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  maxWidth = '4xl',
  footer,
  className = ''
}) => {
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position and disable scrolling on main content
      const scrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.paddingRight = '15px'; // Prevent layout shift
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }
    return () => {
      // Restore scrolling and position
      const scrollY = document.body.style.top;
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, [isOpen]);

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[200] overflow-y-auto"
      onClick={handleClickOutside}
    >
      <div className="min-h-full p-2 md:p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`bg-card/95 backdrop-blur-sm rounded-lg w-full ${maxWidthClasses[maxWidth]} mx-auto shadow-2xl border border-primary/20 my-2 md:my-4 z-[201] ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {(title || showCloseButton) && (
            <div className="flex justify-between items-center p-3 md:p-4 bg-card/95 backdrop-blur-sm border-b border-primary/10">
              {title && (
                <h2 className="text-base md:text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-secondary/80 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <div className="p-3 md:p-4">
            {children}
          </div>

          {footer && (
            <div className="border-t border-primary/10">
              {footer}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}; 